import { cn } from '@/utils/cn';
import DOMPurify from 'dompurify';
import { useMemo, type FC } from 'react';

interface HtmlContentProps {
  html: string;
  className?: string;
}

// Renders BM-configured information texts, which may contain HTML (BR-57160).
// Content is sanitized with DOMPurify; `target` is kept so configured links
// can open in a new tab, and a hook forces rel="noopener noreferrer" on them.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const HtmlContent: FC<HtmlContentProps> = ({ html, className }) => {
  const sanitized = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        ADD_ATTR: ['target'],
        // Information texts are typographic content only — strip interactive
        // and form elements (e.g. a configured <button>) so they can't render
        // as clickable UI inside the text. Their inner text is kept (BR-57160).
        FORBID_TAGS: [
          'button',
          'input',
          'select',
          'textarea',
          'form',
          'style',
          'iframe',
          'object',
          'embed',
        ],
      }),
    [html]
  );

  return (
    <div
      className={cn(
        // Tailwind preflight strips element styles; restore the subset the
        // information texts are expected to use.
        '[&_a]:text-primary [&_a]:underline',
        '[&_h1]:text-lg [&_h1]:font-bold',
        '[&_h2]:text-base [&_h2]:font-bold',
        '[&_h3]:text-sm [&_h3]:font-bold',
        '[&_h1]:mb-1 [&_h1]:mt-2 [&_h2]:mb-1 [&_h2]:mt-2 [&_h3]:mb-1 [&_h3]:mt-2',
        '[&_p:last-child]:mb-0 [&_p]:mb-2',
        '[&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5',
        '[&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5',
        className
      )}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

export default HtmlContent;
